<template>
  <div>
    <header class="db-page-head">
      <div>
        <h2 class="db-page-title">{{ caritas ? "Noticias de Cáritas" : "Noticias" }}</h2>
        <p class="db-page-subtitle">
          {{ caritas ? "Crea y gestiona las noticias de Cáritas parroquial." : "Crea, edita y publica las noticias de la parroquia." }}
        </p>
      </div>
      <div class="db-actions">
        <button type="button" class="db-btn" @click="showModal()">
          <span class="material-symbols-outlined">add</span> Nueva noticia
        </button>
      </div>
    </header>

    <section class="db-card">
      <div class="db-toolbar">
        <label class="db-search">
          <span class="material-symbols-outlined">search</span>
          <input v-model.trim="search" type="search" placeholder="Buscar por título…" aria-label="Buscar noticia" />
        </label>
        <div class="db-segmented" role="group" aria-label="Filtrar por estado">
          <button
            v-for="f in filtros"
            :key="f.key"
            type="button"
            :class="{ 'is-active': filtro === f.key }"
            @click="filtro = f.key"
          >
            {{ f.label }}
          </button>
        </div>
        <span class="db-toolbar-spacer"></span>
        <span v-if="!loading" class="db-count">{{ filtradas.length }} de {{ noticias.length }}</span>
      </div>

      <div v-if="loading" class="db-card-body pn-skeletons">
        <div v-for="n in 5" :key="n" class="db-skeleton" style="height: 52px"></div>
      </div>

      <div v-else-if="!filtradas.length" class="db-empty">
        <span class="material-symbols-outlined">article</span>
        <p v-if="noticias.length">No hay noticias que coincidan con el filtro.</p>
        <template v-else>
          <p>Todavía no hay noticias.</p>
          <button type="button" class="db-btn db-btn--sm" @click="showModal()">Crear la primera</button>
        </template>
      </div>

      <div v-else class="db-table-wrap">
        <table class="db-table">
          <thead>
            <tr>
              <th class="db-col-thumb db-hide-sm"><span class="sr-only">Portada</span></th>
              <th>Título</th>
              <th>Estado</th>
              <th class="db-hide-sm">Fecha</th>
              <th v-if="caritas" class="db-hide-sm">Portada web</th>
              <th class="db-col-actions"><span class="sr-only">Acciones</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="n in filtradas" :key="n.id">
              <td class="db-hide-sm">
                <span class="db-thumb">
                  <img v-img-fallback v-if="n.imagenDePortada" :src="`${base}/img/minGallery/${n.imagenDePortada}`" alt="" loading="lazy" />
                  <span v-else class="material-symbols-outlined">image</span>
                </span>
              </td>
              <td>
                <button type="button" class="pn-title" @click="showModal(n.id)">
                  <span class="db-cell-title">{{ n.titulo || "(Sin título)" }}</span>
                </button>
                <span v-if="n.galeria && n.galeria != 0" class="db-cell-sub">
                  <span class="material-symbols-outlined pn-inline-icon">photo_library</span> {{ nombreGaleria(n.galeria) }}
                </span>
              </td>
              <td>
                <span class="db-badge" :class="esPublicada(n) ? 'db-badge--success' : ''">
                  <span class="material-symbols-outlined">{{ esPublicada(n) ? "public" : "edit_note" }}</span>
                  {{ esPublicada(n) ? "Publicada" : "Borrador" }}
                </span>
              </td>
              <td class="db-hide-sm">{{ formatFecha(n.fechaDePublicacion) }}</td>
              <td v-if="caritas" class="db-hide-sm">
                <span v-if="n.paginaPrincipal == 1" class="db-badge db-badge--info">
                  <span class="material-symbols-outlined">home</span> Visible
                </span>
                <span v-else class="db-badge">No</span>
              </td>
              <td class="db-col-actions">
                <button type="button" class="db-icon-btn" title="Editar" aria-label="Editar" @click="showModal(n.id)">
                  <span class="material-symbols-outlined">edit</span>
                </button>
                <button
                  type="button"
                  class="db-icon-btn db-icon-btn--danger"
                  title="Eliminar"
                  aria-label="Eliminar"
                  @click="deleteNoticia(n)"
                >
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <editor-de-noticias
      :noticia="getNoticia"
      :galerias="galerias"
      :modalId="modalId"
      @changeNews="changeNews"
    />
  </div>
</template>

<script>
import axios from "@/directives/axios.js";
import editorDeNoticias from "@/components/dashboard/ModalEditorDeNoticias.vue";
import {
  confirmar,
  avisar,
  formatFecha,
  parseFecha,
  normalizar,
  avisarError,
} from "@/directives/dbDialogs.js";

export default {
  name: "PanelNoticias",
  components: {
    editorDeNoticias,
  },
  props: {
    caritas: Boolean,
  },
  data() {
    return {
      noticias: [],
      galerias: [],
      getNoticia: undefined,
      loading: true,
      search: "",
      filtro: "todas",
      filtros: [
        { key: "todas", label: "Todas" },
        { key: "publicadas", label: "Publicadas" },
        { key: "borradores", label: "Borradores" },
      ],
      base: this.$media,
    };
  },
  computed: {
    api() {
      return this.caritas ? "/noticiasCaritas" : "/noticias";
    },
    modalId() {
      // El editor usa este id para saber a qué API guardar
      return this.caritas ? "editorDeNoticiasCaritas" : "editorDeNoticias";
    },
    filtradas() {
      const q = normalizar(this.search);
      return this.noticias.filter((n) => {
        if (this.filtro === "publicadas" && !this.esPublicada(n)) return false;
        if (this.filtro === "borradores" && this.esPublicada(n)) return false;
        return !q || normalizar(n.titulo).includes(q);
      });
    },
  },
  methods: {
    formatFecha,
    esPublicada(n) {
      return Number(n.estadoNoticia) === 1 || n.estadoNoticia === true;
    },
    nombreGaleria(id) {
      const g = this.galerias.find((x) => x.id == id);
      return g ? g.titulo : "Galería vinculada";
    },
    showModal(idNoticia) {
      this.getNoticia = idNoticia ? this.noticias.find((n) => n.id === idNoticia) : undefined;
      this.$nextTick(() => this.$bvModal.show(this.modalId));
    },
    async deleteNoticia(noticia) {
      const ok = await confirmar(this, `Se eliminará «${noticia.titulo}». Esta acción no se puede deshacer.`, {
        titulo: "Eliminar noticia",
        ok: "Eliminar",
        peligro: true,
      });
      if (!ok) return;
      try {
        await axios.delete(`${this.api}/eliminarNoticia?id=${noticia.id}`);
        avisar(this, `Se ha eliminado «${noticia.titulo}».`, "success", "Noticia eliminada");
        this.changeNews();
      } catch (error) {
        avisarError(this, error, "No se pudo eliminar la noticia.");
      }
    },
    async changeNews() {
      try {
        const [galerias, noticias] = await Promise.all([
          axios.get("/imagenes/todasGalerias"),
          axios.get(`${this.api}/todasNoticias`),
        ]);
        this.galerias = galerias.data.todasGalerias || [];
        this.noticias = (noticias.data.todasNoticias || [])
          .map((element) => ({
            id: element.id,
            estadoNoticia: element.estadoNoticia,
            titulo: element.titulo,
            noticia: element.resumenNoticia,
            fechaDePublicacion: element.fechaPublicacion,
            galeria: element.idgaleria,
            imagenDePortada: element.imagenPortada,
            edicionNew: element.id,
            resumenNoticia: element.htmlText,
            imagenesNoticia: (element.imagenesNoticia || []).map((img) => img.nombre_imagen),
            paginaPrincipal: element.paginaPrincipal,
          }))
          .sort((a, b) => (parseFecha(b.fechaDePublicacion) || 0) - (parseFecha(a.fechaDePublicacion) || 0));
      } catch (error) {
        avisarError(this, error, "No se pudieron cargar las noticias.");
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.changeNews();
  },
};
</script>

<style scoped>
.pn-skeletons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pn-title {
  padding: 0;
  border: 0;
  background: none;
  color: var(--sd-text);
  text-align: left;
  cursor: pointer;
}

.pn-title:hover .db-cell-title {
  color: var(--sd-primary);
}

.pn-inline-icon {
  font-size: 14px !important;
  vertical-align: -2px;
}
</style>
